import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ConfirmationDialogComponent,
  ConfirmDialogData,
} from '@lockton-apps/shared/ui/lui';
import { MatDialog } from '@angular/material/dialog';
import { ProposalsHooksService } from '../proposals-hooks.service';
import {
  ApplicationData,
  ApplicationHooksData,
  Proposal,
  ProposalPermissionsDef,
  ProposalPermissionStatus,
} from '@lockton-apps/shared/interfaces';
import { FormGroup } from '@angular/forms';

interface LsosHooksData {
  isBroker: boolean;
  reSubmit: boolean;
}

@Injectable()
export class LsosApplicationHooksService extends ProposalsHooksService<LsosHooksData> {
  private readonly beforeSubmitDialogData = {
    broker: {
      content: `<p class="mb-2" i18n>
                  You have made some changes to the client proposal form. Once submitted you
                  will receive an updated quote and application.
                </p>`,
      confirmButtonText: $localize`Yes, Re-quote`,
      cancelButtonText: $localize`Cancel`,
    },
    contact: {
      content: `<p class="mb-2" i18n>
                  You have made some changes to your proposal form and are about to re-submit.
                  <strong>
                    Please ensure all details edited are correct to your knowledge.
                  </strong>
                </p>
                <p i18n>
                  Once you have re-submitted the proposal form your broker will review the
                  changes and send an updated application and quote.
                </p>`,
      confirmButtonText: $localize`Yes, Re-submit`,
      cancelButtonText: $localize`Cancel`,
    },
  };

  constructor(private readonly dialog: MatDialog) {
    super();
  }

  override data: ApplicationHooksData = {
    deactivateDialog: this.deactivateDialogData,
  };

  override permissionsRegistry: ProposalPermissionsDef[] = [
    {
      type: 'readonly',
      source: 'feeIncomeSourceOfFees.grossIncome',
      target: 'areasOfPractice.areasOfPractice',
      validator: (form: FormGroup) => {
        return form.valid
          ? ProposalPermissionStatus.Disabled
          : ProposalPermissionStatus.Enabled;
      },
    },
  ];

  async beforeSave(
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal
  ): Promise<Proposal> {
    return this.handlePermissions(proposal, form);
  }

  async beforeStepSubmit(
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal
  ): Promise<Proposal> {
    return this.handlePermissions(proposal, form);
  }

  async beforeSubmit(
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal
  ): Promise<Proposal> {
    const isConfirmed: boolean =
      !this.meta.reSubmit ||
      (await this.openConfirmationDialog(
        this.beforeSubmitDialogData[this.meta.isBroker ? 'broker' : 'contact']
      ));

    return isConfirmed ? Promise.resolve(proposal) : Promise.reject();
  }

  private openConfirmationDialog(data: ConfirmDialogData): Promise<boolean> {
    return firstValueFrom(
      this.dialog.open(ConfirmationDialogComponent, { data }).afterClosed()
    );
  }
}
